import { NextgenImage } from '../NextgenImage/NextgenImage';
import GenericLink from '../GenericLink/GenericLink';
import { useDeviceTypeMediaQuery } from '../../hooks';
import { GridviewImagePropTypes, ImagePropsType, CTAConfig } from './types/propsType';
import styles from './GridViewImageGallery.module.scss';

/**
 *  GridViewImageGallery that displays images.
 *  It allows users to specify the number of images per row and
 *  supports customizable call-to-action (CTA) links for image.
 *
 * @prop {ImagePropsType[]} images
 * - An array of objects representing the images to display in the grid.
 * - Each object includes `src`, `alt`, and an optional `ctaConfig` for clickable CTAs.
 */

/**
 * @prop {string} imagesPerRow
 * - Specifies how many images should appear in each row of the grid.
 * - Automatically adjusted for mobile layouts.
 */

/**
 * @prop {Object} restProps
 * - Additional props passed to the `NextgenImage` component for customization.
 * - Includes attributes like styling or other HTML properties.
 */

const GridViewImageGallery = ({ images, imagesPerRow, ...restProps }: GridviewImagePropTypes) => {
  const { isMobile } = useDeviceTypeMediaQuery();
  const gridStyle = {
    gridTemplateColumns: `repeat(${isMobile ? 1 : parseInt(imagesPerRow)}, 1fr)`,
  };

  // specific to grid image
  const renderImage = (image: ImagePropsType) => {
    if (image?.ctaConfig?.href) {
      return (
        <GenericLink key={image.src} href={image?.ctaConfig?.href ?? ''} target={image?.ctaConfig?.target}>
          <div className={styles['gridview-image-container']}>
            <NextgenImage
              {...restProps}
              src={image.src}
              alt={image.alt}
              className={styles['gridview-image']}
              loading="lazy"
              decoding="async"
            />
          </div>
        </GenericLink>
      );
    } else {
      return (
        <div key={`${image.src}-${image.alt}`} className={styles['gridview-image-container']}>
          <NextgenImage
            {...restProps}
            src={image.src}
            alt={image.alt}
            className={styles['gridview-image']}
            loading="lazy"
            decoding="async"
          />
        </div>
      );
    }
  };

  return (
    <div className={`${styles['gridview-image-root']}`} style={gridStyle} data-testid="grid-container">
      {images.map(renderImage)}
    </div>
  );
};

export type { GridviewImagePropTypes, ImagePropsType, CTAConfig };
export default GridViewImageGallery;
